<script
	module
	lang="ts"
>
	export type Entity = {
		id: number
		active: boolean
		storeCode: string | null
		companyName: string
		contactName: string
		email: string
		phoneNumber: string
		streetAddress: string | null
	}

	type MergeOptionType = 'INFORMATION' | 'DOCUMENTS'

	//end of module section
</script>

<script lang="ts">
	import { getEventChecked } from '@isoftdata/browser-event'
	import type { MergeCustomerOptionsInput, MergeVendorsOptionsInput } from '$houdini'

	import Checkbox from '@isoftdata/svelte-checkbox'
	import Card from '@isoftdata/svelte-card'
	import Fieldset from '@isoftdata/svelte-fieldset'
	import Icon from '@isoftdata/svelte-icon'
	import Modal from '@isoftdata/svelte-modal'

	interface Props {
		title: string
		entityType: 'customer' | 'vendor'
		mergeFn?:
			| ((
					masterEntityId: number,
					sourceEntityIds: number[],
					options: MergeCustomerOptionsInput | MergeVendorsOptionsInput,
			  ) => Promise<void>)
			| undefined
	}

	let { title, entityType, mergeFn = undefined }: Props = $props()

	// default all merge options to true
	const customerMergeOptions: Record<
		keyof MergeCustomerOptionsInput,
		{ label: string; value: boolean; type: MergeOptionType }
	> = $state({
		addresses: { label: 'Alt. Addresses', value: true, type: 'INFORMATION' },
		attachments: { label: 'Attached Files', value: true, type: 'INFORMATION' },
		contacts: { label: 'Contacts', value: true, type: 'INFORMATION' },
		options: { label: 'Customer Options', value: true, type: 'INFORMATION' },
		priceContracts: { label: 'Item Price Contracts', value: true, type: 'INFORMATION' },
		messages: { label: 'Messages', value: true, type: 'INFORMATION' },
		paymentMethods: { label: 'Payment Methods', value: true, type: 'INFORMATION' },
		payments: { label: 'Payments', value: true, type: 'DOCUMENTS' },
		sellPriceClasses: { label: 'Price Classes', value: true, type: 'INFORMATION' },
		salesOrders: { label: 'Sales Orders', value: true, type: 'DOCUMENTS' },
		taxExemptions: { label: 'Tax Exemptions', value: true, type: 'INFORMATION' },
		units: { label: 'Units', value: true, type: 'INFORMATION' },
		workOrders: { label: 'Work Orders', value: true, type: 'DOCUMENTS' },
	})
	const vendorMergeOptions: Record<
		keyof MergeVendorsOptionsInput,
		{ label: string; value: boolean; type: MergeOptionType }
	> = $state({
		addresses: { label: 'Alt. Addresses', value: true, type: 'INFORMATION' },
		attachments: { label: 'Attached Files', value: true, type: 'INFORMATION' },
		messages: { label: 'Messages', value: true, type: 'INFORMATION' },
		purchaseOrders: { label: 'Purchase Orders', value: true, type: 'DOCUMENTS' },
		catalogs: { label: 'Vendor Catalogs', value: true, type: 'INFORMATION' },
		options: { label: 'Vendor Options', value: true, type: 'INFORMATION' },
	})

	let show: boolean = $state(false)
	let isMerging: boolean = $state(false)
	let showMergeOptions: boolean = $state(false)
	let selectedEntityList: Entity[] = $state([])
	let selectedMasterEntityId: number | null = $state(null)
	let entityName = $derived(entityType === 'customer' ? 'Customer' : 'Vendor')
	let errorMessage: string = $state('')

	let showStatusBadge = $derived(!selectedEntityList.every(entity => entity.active))
	let sourceList = $derived(selectedEntityList.filter(entity => entity.id !== selectedMasterEntityId))
	let selectedMasterEntity = $derived(selectedEntityList.find(entity => entity.id === selectedMasterEntityId))
	let numberOfCheckedMergeOptions = $derived(
		entityType === 'customer'
			? Object.values(customerMergeOptions).filter(option => option.value).length
			: Object.values(vendorMergeOptions).filter(option => option.value).length,
	)

	export function open(entities: Entity[]) {
		// move those active entities to the top of the list
		// this is to make sure an active entity is always selected first
		selectedEntityList = entities.sort(entity => (entity.active ? -1 : 1))
		// then select the first one
		selectedMasterEntityId = selectedEntityList[0].id
		show = true
	}

	function close() {
		show = false
		showMergeOptions = false
		selectedMasterEntityId = null
		errorMessage = ''
	}

	function getMergeOptionsInputs(entityType: 'customer'): MergeCustomerOptionsInput
	function getMergeOptionsInputs(entityType: 'vendor'): MergeVendorsOptionsInput
	function getMergeOptionsInputs(
		entityType: 'customer' | 'vendor',
	): MergeCustomerOptionsInput | MergeVendorsOptionsInput
	function getMergeOptionsInputs(
		entityType: 'customer' | 'vendor',
	): MergeCustomerOptionsInput | MergeVendorsOptionsInput {
		if (entityType === 'customer') {
			return {
				addresses: customerMergeOptions.addresses.value,
				attachments: customerMergeOptions.attachments.value,
				contacts: customerMergeOptions.contacts.value,
				messages: customerMergeOptions.messages.value,
				options: customerMergeOptions.options.value,
				paymentMethods: customerMergeOptions.paymentMethods.value,
				payments: customerMergeOptions.payments.value,
				priceContracts: customerMergeOptions.priceContracts.value,
				salesOrders: customerMergeOptions.salesOrders.value,
				sellPriceClasses: customerMergeOptions.sellPriceClasses.value,
				taxExemptions: customerMergeOptions.taxExemptions.value,
				units: customerMergeOptions.units.value,
				workOrders: customerMergeOptions.workOrders.value,
			}
		} else if (entityType === 'vendor') {
			return {
				addresses: vendorMergeOptions.addresses.value,
				attachments: vendorMergeOptions.attachments.value,
				options: vendorMergeOptions.options.value,
				messages: vendorMergeOptions.messages.value,
				purchaseOrders: vendorMergeOptions.purchaseOrders.value,
				catalogs: vendorMergeOptions.catalogs.value,
			}
		} else {
			throw new Error('Invalid entity type')
		}
	}

	async function merge() {
		if (!selectedMasterEntityId) {
			errorMessage = 'Please select a destination entity.'
			return
		}
		// do the merge
		isMerging = true
		try {
			if (!mergeFn) {
				throw new Error('No merge function provided')
			}

			await mergeFn(
				selectedMasterEntityId,
				sourceList.map(entity => entity.id),
				getMergeOptionsInputs(entityType),
			)
			close()
		} catch (error: any) {
			console.error('Error merging:', error)
			errorMessage = `An error occurred while merging: ${error.message}`
		} finally {
			isMerging = false
		}
	}
</script>

<Modal
	{show}
	{title}
	{close}
	modalSize="xl"
	confirmButtonText="Merge"
	cancelButtonText="Cancel"
	confirmButtonIsLoading={isMerging}
	backdropClickCancels={false}
	confirm={merge}
>
	<div class="card-deck mb-2">
		<div class="card">
			<div class="card-header">
				<h6 class="mb-0">Select Destination {entityName}</h6>
			</div>
			<div class="card-body">
				<small class="text-muted">*The non-selected ones will get merged into the Destination {entityName}.</small>
				<div
					class="list-group overflow-auto"
					style="max-height: 33vh;"
				>
					{#each selectedEntityList as entity}
						<button
							type="button"
							class="list-group-item list-group-item-action"
							class:active={selectedMasterEntityId && selectedMasterEntityId === entity.id}
							onclick={() => {
								selectedMasterEntityId = entity.id
							}}
						>
							{@render entityDetails(entity)}
						</button>
					{/each}
				</div>
			</div>
		</div>
		<div class="align-content-center text-center">
			<i class="fa-solid fa-merge fa-3x fa-rotate-by rotate-down"></i>
		</div>
		<div class="card">
			<div class="card-header">
				<h6 class="mb-0">Destination {entityName}</h6>
			</div>
			<div class="card-body align-content-center">
				{#if selectedMasterEntity}
					{@render entityDetails(selectedMasterEntity)}
					<hr class="my-1" />
					<span class="font-weight-bold">Merging:</span>
					<div class="form-row">
						{#each Object.values(entityType === 'customer' ? customerMergeOptions : vendorMergeOptions) as mergeOption}
							{#if mergeOption.value}
								<div class="col-12 col-xl-6">
									<Icon
										icon="check"
										class="mr-1 text-success"
									/>
									<span class="text-ellipsis">
										{mergeOption.label}
									</span>
								</div>
							{/if}
						{/each}
					</div>
				{/if}
			</div>
		</div>
	</div>
	<Card
		collapsible
		entireHeaderToggles
		headerClass="card-header d-flex justify-content-between align-items-center"
		bind:bodyShown={showMergeOptions}
	>
		{#snippet header()}
			<div>
				<h6 class="mb-0">Manage Merge Options</h6>
				<small class="text-muted">{numberOfCheckedMergeOptions} merge options are checked.</small>
			</div>
		{/snippet}

		<div class="form-row">
			{#each ['INFORMATION', 'DOCUMENTS'] as mergeType}
				{@const mergeOptions = entityType === 'customer' ? customerMergeOptions : vendorMergeOptions}
				<div
					class="col-12"
					class:col-lg-9={mergeType === 'INFORMATION'}
					class:col-lg-3={mergeType === 'DOCUMENTS'}
				>
					<Fieldset
						class="h-100"
						label={`${mergeType === 'INFORMATION' ? 'Information' : 'Documents'} To Merge`}
					>
						<div class="form-row">
							{#each Object.entries(mergeOptions) as [key, mergeOption]}
								{#if mergeOption.type === mergeType}
									<div
										class="col-12"
										class:col-sm-6={mergeType === 'INFORMATION'}
										class:col-lg-3={mergeType === 'INFORMATION'}
									>
										<Checkbox
											inline
											label={mergeOption.label}
											checked={mergeOption.value}
											onchange={event => {
												const value = getEventChecked(event)
												if (entityType === 'customer') {
													customerMergeOptions[key].value = value
												} else if (entityType === 'vendor') {
													vendorMergeOptions[key].value = value
												}
											}}
										/>
									</div>
								{/if}
							{/each}
						</div>
					</Fieldset>
				</div>
			{/each}
		</div>
	</Card>

	{#if errorMessage}
		<div
			class="alert alert-danger mt-3"
			role="alert"
		>
			{errorMessage}
		</div>
	{/if}
</Modal>

{#snippet entityDetails(entity)}
	<div class="form-row">
		<div class="col-12 col-lg-6">
			<div class="d-flex flex-column">
				<div>
					<span class="font-weight-bold mr-2">{entity.storeCode ? `${entity.storeCode}-${entity.id}` : entity.id}</span>
					{#if showStatusBadge}
						<span class="badge badge-pill {entity.active ? 'badge-success' : 'badge-danger'}"
							>{entity.active ? 'Active' : 'Inactive'}</span
						>
					{/if}
				</div>
				<span
					class="text-ellipsis"
					class:font-italic={!entity.companyName}
					class:text-muted={!entity.companyName}
				>
					{entity.companyName || 'No Company Name'}
				</span>
				<span
					class="text-ellipsis"
					class:font-italic={!entity.contactName}
					class:text-muted={!entity.contactName}
				>
					{entity.contactName || 'No Contact Name'}
				</span>
			</div>
		</div>
		<div class="col-12 col-lg-6">
			<div class="d-flex flex-column">
				<span class="text-ellipsis">
					Email:
					<span
						class:font-italic={!entity.email}
						class:text-muted={!entity.email}
					>
						{entity.email || 'No Email'}
					</span>
				</span>
				<span class="text-ellipsis">
					Phone:
					<span
						class:font-italic={!entity.phoneNumber}
						class:text-muted={!entity.phoneNumber}
					>
						{entity.phoneNumber || 'No Phone Number'}
					</span>
				</span>
				<span class="text-ellipsis">
					Address:
					<span
						class:font-italic={!entity.streetAddress}
						class:text-muted={!entity.streetAddress}
					>
						{entity.streetAddress || 'No Address'}
					</span>
				</span>
			</div>
		</div>
	</div>
{/snippet}

<style>
	@media (max-width: 666px) {
		.rotate-down {
			--fa-rotate-angle: 90deg;
			margin-bottom: 0.5rem;
		}
	}
</style>
